/**
 * Curated "Insert Symbol" set for scientific data entry (PK parameters etc.)
 * plus a caret-insertion helper. Shared so any text field in the app can offer
 * symbol insertion.
 *
 * IMPORTANT: every symbol here is chosen to survive the text pipeline untouched.
 * The sanitizer (`utils/textSanitization.js` `normalizationMap`) and the backend
 * QC `applyUnicodeConversion` rewrite a set of "normalizable" characters to
 * ASCII -- dashes, superscripts, and the math operators `× ÷ ≤ ≥ ± · ∗ ∕` plus
 * smart quotes. Those are DELIBERATELY excluded here: inserting `≥` would just
 * become `>=` downstream, which would mislead the user. Greek letters, accented
 * Latin letters, and the misc glyphs below have no such mapping, so they
 * round-trip cleanly.
 *
 * The unit test (`web/unit-tests/symbols.test.js`) asserts this invariant by
 * running every symbol through the real `applyUnicodeConversion` and requiring
 * no change -- so a future edit that adds a normalized character will fail CI.
 */

// Lowercase Greek -- the bulk of PK parameter notation (alpha, beta, mu, ...).
export const GREEK_LOWERCASE = [
	{ char: "α", name: "alpha" },
	{ char: "β", name: "beta" },
	{ char: "γ", name: "gamma" },
	{ char: "δ", name: "delta" },
	{ char: "ε", name: "epsilon" },
	{ char: "ζ", name: "zeta" },
	{ char: "η", name: "eta" },
	{ char: "θ", name: "theta" },
	{ char: "κ", name: "kappa" },
	{ char: "λ", name: "lambda" },
	{ char: "μ", name: "mu" },
	{ char: "ν", name: "nu" },
	{ char: "ξ", name: "xi" },
	{ char: "π", name: "pi" },
	{ char: "ρ", name: "rho" },
	{ char: "σ", name: "sigma" },
	{ char: "τ", name: "tau" },
	{ char: "φ", name: "phi" },
	{ char: "χ", name: "chi" },
	{ char: "ψ", name: "psi" },
	{ char: "ω", name: "omega" },
];

// Uppercase Greek, limited to the forms that are visually distinct from Latin
// capitals (so users don't confuse "Α" with "A"). Delta/Sigma/Omega are
// the common ones in this domain.
export const GREEK_UPPERCASE = [
	{ char: "Γ", name: "Gamma" },
	{ char: "Δ", name: "Delta" },
	{ char: "Θ", name: "Theta" },
	{ char: "Λ", name: "Lambda" },
	{ char: "Ξ", name: "Xi" },
	{ char: "Π", name: "Pi" },
	{ char: "Σ", name: "Sigma" },
	{ char: "Φ", name: "Phi" },
	{ char: "Ψ", name: "Psi" },
	{ char: "Ω", name: "Omega" },
];

// Accented Latin letters - common in author names and affiliations (French,
// German, Spanish, Portuguese, Nordic...). These are a frequent member of the
// "dropped glyph" long tail and round-trip unchanged through the sanitizer.
export const ACCENTED_LOWERCASE = [
	{ char: "à", name: "a grave" },
	{ char: "á", name: "a acute" },
	{ char: "â", name: "a circumflex" },
	{ char: "ä", name: "a umlaut" },
	{ char: "ã", name: "a tilde" },
	{ char: "å", name: "a ring" },
	{ char: "æ", name: "ae" },
	{ char: "ç", name: "c cedilla" },
	{ char: "è", name: "e grave" },
	{ char: "é", name: "e acute" },
	{ char: "ê", name: "e circumflex" },
	{ char: "ë", name: "e umlaut" },
	{ char: "ì", name: "i grave" },
	{ char: "í", name: "i acute" },
	{ char: "î", name: "i circumflex" },
	{ char: "ï", name: "i umlaut" },
	{ char: "ñ", name: "n tilde" },
	{ char: "ò", name: "o grave" },
	{ char: "ó", name: "o acute" },
	{ char: "ô", name: "o circumflex" },
	{ char: "ö", name: "o umlaut" },
	{ char: "õ", name: "o tilde" },
	{ char: "ø", name: "o slash" },
	{ char: "œ", name: "oe" },
	{ char: "ù", name: "u grave" },
	{ char: "ú", name: "u acute" },
	{ char: "û", name: "u circumflex" },
	{ char: "ü", name: "u umlaut" },
	{ char: "ý", name: "y acute" },
	{ char: "ÿ", name: "y umlaut" },
	{ char: "ß", name: "sharp s" },
];

export const ACCENTED_UPPERCASE = [
	{ char: "À", name: "A grave" },
	{ char: "Á", name: "A acute" },
	{ char: "Â", name: "A circumflex" },
	{ char: "Ä", name: "A umlaut" },
	{ char: "Ã", name: "A tilde" },
	{ char: "Å", name: "A ring" },
	{ char: "Æ", name: "AE" },
	{ char: "Ç", name: "C cedilla" },
	{ char: "È", name: "E grave" },
	{ char: "É", name: "E acute" },
	{ char: "Ê", name: "E circumflex" },
	{ char: "Ë", name: "E umlaut" },
	{ char: "Í", name: "I acute" },
	{ char: "Î", name: "I circumflex" },
	{ char: "Ï", name: "I umlaut" },
	{ char: "Ñ", name: "N tilde" },
	{ char: "Ò", name: "O grave" },
	{ char: "Ó", name: "O acute" },
	{ char: "Ô", name: "O circumflex" },
	{ char: "Ö", name: "O umlaut" },
	{ char: "Õ", name: "O tilde" },
	{ char: "Ø", name: "O slash" },
	{ char: "Œ", name: "OE" },
	{ char: "Ù", name: "U grave" },
	{ char: "Ú", name: "U acute" },
	{ char: "Û", name: "U circumflex" },
	{ char: "Ü", name: "U umlaut" },
];

// Misc scientific + typographic glyphs that are NOT normalized away. Operators
// like ± × ≤ ≥ are intentionally absent -- they get rewritten to ASCII
// downstream (and the sanitization dialog offers those as quick-picks).
export const MISC_SYMBOLS = [
	{ char: "°", name: "degree" },
	{ char: "′", name: "prime" },
	{ char: "″", name: "double prime" },
	{ char: "‰", name: "per mille" },
	{ char: "∞", name: "infinity" },
	{ char: "√", name: "square root" },
	{ char: "≈", name: "approximately equal" },
	{ char: "≠", name: "not equal" },
	{ char: "∼", name: "tilde operator" },
	{ char: "∝", name: "proportional to" },
	{ char: "•", name: "bullet" },
	{ char: "…", name: "ellipsis" },
	{ char: "†", name: "dagger" },
	{ char: "‡", name: "double dagger" },
	{ char: "§", name: "section" },
	{ char: "¶", name: "pilcrow" },
	{ char: "©", name: "copyright" },
	{ char: "®", name: "registered" },
	{ char: "™", name: "trademark" },
];

export const SYMBOL_GROUPS = [
	{ label: "Accented (lowercase)", symbols: ACCENTED_LOWERCASE },
	{ label: "Accented (uppercase)", symbols: ACCENTED_UPPERCASE },
	{ label: "Greek (lowercase)", symbols: GREEK_LOWERCASE },
	{ label: "Greek (uppercase)", symbols: GREEK_UPPERCASE },
	{ label: "Other", symbols: MISC_SYMBOLS },
];

/** Flat list of every offered symbol character (used by tests). */
export const ALL_SYMBOLS = SYMBOL_GROUPS.flatMap((g) =>
	g.symbols.map((s) => s.char),
);

/**
 * Insert `char` into `currentValue` at the given selection range, replacing any
 * selected text. Pure + framework-agnostic so it's unit-testable and reusable
 * across field types; the caller supplies the input's selection and applies the
 * returned value + caret.
 *
 * @param {string} currentValue - The field's current value.
 * @param {number|null} start - selectionStart (falls back to end of string).
 * @param {number|null} end - selectionEnd (falls back to `start`).
 * @param {string} char - The symbol to insert.
 * @returns {{ value: string, caret: number }} New value and caret position.
 */
export function insertAtCaret(currentValue, start, end, char) {
	const s = String(currentValue ?? "");
	const a = Number.isInteger(start) ? start : s.length;
	const b = Number.isInteger(end) ? end : a;
	const lo = Math.max(0, Math.min(a, b));
	const hi = Math.max(0, Math.max(a, b));
	const value = s.slice(0, lo) + char + s.slice(hi);
	return { value, caret: lo + char.length };
}
